import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Ban, Search, AlertTriangle, ThumbsUp, CheckCircle2, XCircle, ExternalLink, Shield, TrendingUp, ArrowRight } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * DNPPage — community-sourced Do-Not-Pay broker blacklist.
 *
 * Route: /dnp
 *
 * Two modes:
 *   - Search: paste MC# / DOT# / broker name, see approved reports
 *   - Report: submit a new report (goes to `pending`, admin-moderated)
 *
 * Anti-abuse: reports moderated before publication. Reporter identity always
 * hidden. Upvote button records IP to prevent one person from stacking votes.
 */
export default function DNPPage() {
  const [tab, setTab] = useState("search");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios.get(`${API_BASE}/api/dnp/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Nav */}
      <div className="border-b border-white/10 bg-neutral-950/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-1" data-testid="dnp-home-link">
            <span className="font-black text-xl tracking-tight">Freight</span>
            <span className="font-black text-xl tracking-tight text-[#FF5A1F]">Pilot</span>
            <span className="font-black text-xl tracking-tight">AI</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/tools" className="text-white/70 hover:text-white transition-colors hidden sm:inline" data-testid="dnp-tools-link">
              All Tools →
            </Link>
            <Link to="/" className="text-white/70 hover:text-white transition-colors" data-testid="dnp-back-link">
              ← Home
            </Link>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FF5A1F 0%, transparent 40%), radial-gradient(circle at 80% 70%, #FF5A1F 0%, transparent 40%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 pb-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-1.5 text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-6">
            <Ban className="w-3.5 h-3.5" />
            Community-sourced · Moderated · Free
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight mb-5">
            The <span className="text-[#FF5A1F]">Do-Not-Pay</span>
            <br />
            broker database.
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto mb-6 leading-relaxed">
            Search any broker before you accept their load. If they've ghosted a carrier, disputed an invoice, or double-brokered, you'll find them here. Every report is submitted by working carriers and reviewed by us before it goes public.
          </p>

          {/* Stats */}
          {stats && (
            <div className="inline-flex items-center gap-6 text-sm bg-white/5 border border-white/10 rounded-full px-6 py-2.5 mb-2">
              <span><b className="text-[#FF5A1F]">{stats.approved_reports}</b> approved reports</span>
              <span className="text-white/30">·</span>
              <span><b className="text-white">${(stats.total_amount_reported_lost || 0).toLocaleString()}</b> total losses reported</span>
            </div>
          )}
        </div>
      </section>

      {/* Tab switcher */}
      <section className="max-w-4xl mx-auto px-6 mb-8">
        <div className="flex gap-2 border-b border-white/10">
          <TabBtn active={tab === "search"} onClick={() => setTab("search")} testid="dnp-tab-search">
            <Search className="w-4 h-4" /> Search Database
          </TabBtn>
          <TabBtn active={tab === "report"} onClick={() => setTab("report")} testid="dnp-tab-report">
            <AlertTriangle className="w-4 h-4" /> Report a Broker
          </TabBtn>
        </div>
      </section>

      {tab === "search" ? <SearchTab /> : <ReportTab onSubmitted={() => setTab("search")} />}

      {/* Pair with other tools */}
      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sm:p-8">
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-4 text-center">
            Complete broker verification workflow
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <ToolLink to="/broker-check" icon={<Shield className="w-5 h-5" />} title="1. BrokerCheck" body="Verify their MC# is real (FMCSA)" />
            <ToolLink to="/dnp" icon={<Ban className="w-5 h-5" />} title="2. Broker Directory" body="Check community reports" active />
            <ToolLink to="/rate-check" icon={<TrendingUp className="w-5 h-5" />} title="3. RateCheck" body="Confirm the rate is fair" />
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="border-t border-white/10 bg-neutral-900/50">
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <h3 className="text-2xl sm:text-3xl font-black mb-3 tracking-tight">
            Verify before you haul — every time.
          </h3>
          <p className="text-white/70 max-w-xl mx-auto mb-6">
            FreightPilotAI's concierge setup includes automatic broker screening before every load. So you never accept work from a broker with a bad payment history again.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-6 py-3 transition-colors" data-testid="dnp-cta-signup">
            Set It Up For Me — Free
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-white/50">
          <div>© 2026 <b className="text-white">FreightPilotAI</b> · DNP is a community tool. Not affiliated with FMCSA. Reports are user opinions, moderated before publication.</div>
          <div className="flex items-center gap-4">
            <Link to="/tools" className="hover:text-white">All Tools</Link>
            <Link to="/contact" className="hover:text-white">Dispute a report</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ─────────────────── Search tab ─────────────────── */
function SearchTab() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API_BASE}/api/dnp/search`, { params: { q: query.trim() } });
      setResults(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Search failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const upvote = async (id) => {
    try {
      await axios.post(`${API_BASE}/api/dnp/upvote`, { report_id: id });
      // Optimistically bump the count locally
      setResults((r) => ({
        ...r,
        results: r.results.map((x) => (x.id === id ? { ...x, upvotes: x.upvotes + 1 } : x)),
      }));
    } catch (err) {
      alert(err.response?.data?.detail || "Upvote failed");
    }
  };

  return (
    <section className="max-w-4xl mx-auto px-6 pb-8">
      <form onSubmit={runSearch} className="mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by MC#, DOT#, or broker name..."
            className="w-full bg-white/5 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-xl px-5 py-4 pl-12 text-lg text-white placeholder-white/40 outline-none transition-colors"
            data-testid="dnp-search-input"
          />
          <button type="submit" disabled={loading || !query.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#FF5A1F] hover:bg-[#FF7038] disabled:bg-white/10 disabled:text-white/40 text-black font-bold rounded-lg px-6 py-2.5 text-sm transition-colors"
            data-testid="dnp-search-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Search"}
          </button>
        </div>
      </form>

      {error && (
        <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-6 text-center">
          <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {results && (
        <div data-testid="dnp-results">
          {results.results.length === 0 ? (
            <div className="rounded-xl border-2 border-emerald-500/40 bg-emerald-500/10 p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-2">Result</div>
              <h2 className="text-2xl font-black mb-2 text-emerald-100">No reports found</h2>
              <p className="text-emerald-200/80 text-sm max-w-md mx-auto">
                No approved reports match "<b>{results.query}</b>". This isn't a guarantee they're safe — always verify with <Link to="/broker-check" className="underline">BrokerCheck</Link> and check their insurance certificate before hauling.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-white/50">
                Found <b className="text-white">{results.count}</b> report{results.count !== 1 ? "s" : ""} matching "<b className="text-white">{results.query}</b>"
              </div>
              {results.results.map((r) => (
                <ReportCard key={r.id} r={r} onUpvote={() => upvote(r.id)} />
              ))}
            </div>
          )}
          <div className="mt-6 pt-4 border-t border-white/10 text-[11px] text-white/40 leading-snug">
            {results.disclaimer}
          </div>
        </div>
      )}

      {!results && !error && (
        <div className="rounded-xl border border-white/10 bg-white/5 p-8 text-center">
          <Ban className="w-10 h-10 text-white/30 mx-auto mb-3" />
          <div className="text-white/60 mb-1">Type a broker MC#, DOT#, or name to search</div>
          <div className="text-white/40 text-sm">Empty database on launch. As real carriers submit reports, this will grow.</div>
        </div>
      )}
    </section>
  );
}

/* ─────────────────── Report tab ─────────────────── */
function ReportTab({ onSubmitted }) {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState({
    broker_name: "", broker_mc: "", broker_dot: "", incident_type: "no_pay",
    incident_date: "", amount_lost: "", description: "", reporter_email: "", reporter_carrier_name: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API_BASE}/api/dnp/incident-types`).then((r) => setTypes(r.data)).catch(() => {});
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.broker_name.trim() || (!form.broker_mc.trim() && !form.broker_dot.trim()) || !form.description.trim()) {
      setError("Fill in the broker name, at least one of MC/DOT, and a description of what happened.");
      return;
    }
    if (form.description.trim().length < 30) {
      setError("Description must be at least 30 characters — help us take this report seriously.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.reporter_email) delete payload.reporter_email;
      if (!payload.amount_lost) delete payload.amount_lost;
      else payload.amount_lost = parseFloat(payload.amount_lost);
      const { data } = await axios.post(`${API_BASE}/api/dnp/report`, payload);
      setSuccess(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.response?.data?.detail || "Submission failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <div className="rounded-xl border-2 border-emerald-500/60 bg-emerald-500/10 p-8 text-center" data-testid="dnp-report-success">
          <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
          <h2 className="text-2xl font-black mb-2 text-emerald-100">Report submitted</h2>
          <p className="text-emerald-200/80 mb-4 max-w-md mx-auto">{success.message}</p>
          <div className="text-xs text-emerald-200/60 mb-6">Report ID: <span className="font-mono">{success.report_id}</span></div>
          <button onClick={onSubmitted}
            className="bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-6 py-2.5 transition-colors"
            data-testid="dnp-report-done">
            Back to search
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-3xl mx-auto px-6 pb-8">
      <form onSubmit={submit} className="rounded-2xl border-2 border-white/10 bg-white/5 p-6 sm:p-8 space-y-4" data-testid="dnp-report-form">
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-4 text-sm text-amber-100">
          <div className="font-bold mb-1">⚠️ Before you file</div>
          Only report brokers you've personally had a problem with. False reports hurt the community and expose you to legal risk. Every submission is manually reviewed. Your identity stays private — only your carrier name (if provided) may appear publicly.
        </div>

        {/* Broker info */}
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Broker name (required)" value={form.broker_name} onChange={update("broker_name")} placeholder="ABC Freight Brokers LLC" testid="dnp-form-broker-name" required />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Broker MC#" value={form.broker_mc} onChange={update("broker_mc")} placeholder="MC-123456" testid="dnp-form-broker-mc" />
            <Field label="Broker DOT#" value={form.broker_dot} onChange={update("broker_dot")} placeholder="1234567" testid="dnp-form-broker-dot" />
          </div>
        </div>

        {/* Incident */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1.5">What happened (required)</div>
            <select value={form.incident_type} onChange={update("incident_type")}
              className="w-full bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white outline-none transition-colors"
              data-testid="dnp-form-incident">
              {types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Incident date" type="date" value={form.incident_date} onChange={update("incident_date")} testid="dnp-form-date" />
            <Field label="Amount lost ($)" type="number" value={form.amount_lost} onChange={update("amount_lost")} placeholder="1200" testid="dnp-form-amount" />
          </div>
        </div>

        {/* Description */}
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-1.5">
            What happened — facts only (required, 30+ chars)
          </div>
          <textarea value={form.description} onChange={update("description")} rows={5}
            placeholder="Booked a load Sioux Falls → Chicago on 08/15/2026 at $1,200. Broker disappeared after delivery. Invoice unpaid 45+ days. Multiple emails and calls unanswered. No response to attorney letter."
            className="w-full bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white placeholder-white/30 outline-none transition-colors resize-vertical"
            data-testid="dnp-form-description"
          />
          <div className={`text-[10px] mt-1 ${form.description.length < 30 ? "text-amber-400" : "text-white/40"}`}>
            {form.description.length} / 30 minimum characters
          </div>
        </div>

        {/* Reporter (optional) */}
        <div className="pt-3 border-t border-white/10">
          <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-2">
            Your info (optional — helps establish credibility)
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Your carrier name" value={form.reporter_carrier_name} onChange={update("reporter_carrier_name")} placeholder="Optional — will show publicly" testid="dnp-form-carrier" />
            <Field label="Your email" type="email" value={form.reporter_email} onChange={update("reporter_email")} placeholder="Never shown publicly" testid="dnp-form-email" />
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
            {error}
          </div>
        )}

        <button type="submit" disabled={loading}
          className="w-full bg-[#FF5A1F] hover:bg-[#FF7038] disabled:bg-white/10 disabled:text-white/40 text-black font-bold rounded-lg px-6 py-3 text-base transition-colors flex items-center justify-center gap-2"
          data-testid="dnp-report-submit">
          {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>) : "Submit report for review"}
        </button>

        <p className="text-[11px] text-white/40 text-center leading-snug">
          By submitting, you confirm this report is based on your direct experience with this broker. Reports go through moderation before publication (usually 24-48 hours). We may contact you at the email above (never public) to verify details.
        </p>
      </form>
    </section>
  );
}

/* ─────────────────── Report card ─────────────────── */
function ReportCard({ r, onUpvote }) {
  return (
    <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-5" data-testid="dnp-report-card">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="text-[9px] uppercase tracking-widest bg-red-500/20 border border-red-500/40 text-red-100 font-bold px-2 py-0.5 rounded">
              {r.incident_type_label}
            </span>
            {r.incident_date && (
              <span className="text-[10px] text-white/40">on {r.incident_date}</span>
            )}
          </div>
          <div className="text-lg font-bold text-white">{r.broker_name}</div>
          <div className="text-sm text-white/50 flex flex-wrap gap-x-3">
            {r.broker_mc && <span>{r.broker_mc}</span>}
            {r.broker_dot && <span>DOT {r.broker_dot}</span>}
            {r.amount_lost && <span className="text-red-300">Lost ${r.amount_lost.toLocaleString()}</span>}
          </div>
        </div>
        <button onClick={onUpvote}
          className="shrink-0 flex flex-col items-center gap-0.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#FF5A1F]/40 transition-colors group"
          data-testid={`dnp-upvote-${r.id}`}>
          <ThumbsUp className="w-4 h-4 text-white/60 group-hover:text-[#FF5A1F] transition-colors" />
          <span className="text-xs font-bold text-white">{r.upvotes}</span>
        </button>
      </div>
      <p className="text-sm text-white/70 leading-snug">{r.description}</p>
      {r.reporter_carrier_name && (
        <div className="mt-3 pt-3 border-t border-white/10 text-[11px] text-white/40">
          Reported by <b className="text-white/60">{r.reporter_carrier_name}</b>
        </div>
      )}
    </div>
  );
}

/* ─────────────────── Small helpers ─────────────────── */
function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors border-b-2 ${
        active ? "border-[#FF5A1F] text-white" : "border-transparent text-white/50 hover:text-white/80"
      }`}>
      {children}
    </button>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5">
        {label}
      </div>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required}
        className="w-full bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white placeholder-white/30 outline-none transition-colors"
        data-testid={testid}
      />
    </div>
  );
}

function ToolLink({ to, icon, title, body, active }) {
  return (
    <Link to={to} className={`block rounded-lg border-2 p-4 transition-colors ${
      active ? "border-[#FF5A1F] bg-[#FF5A1F]/10" : "border-white/10 hover:border-white/30 bg-white/5"
    }`}>
      <div className="w-9 h-9 rounded-md bg-[#FF5A1F]/20 text-[#FF5A1F] flex items-center justify-center mb-2">{icon}</div>
      <div className="text-sm font-bold text-white mb-0.5">{title}</div>
      <div className="text-[12px] text-white/60">{body}</div>
    </Link>
  );
}
